/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { Icon, Button } from 'sarsaparilla';

import { getNewId, jsUcfirst } from '../../utils/utilsNew';
import GenericItem from './GenericItem';

const propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
            amount: PropTypes.number,
            removeable: PropTypes.bool,
        })
    ),
    handleListChange: PropTypes.func,
    maxQuota: PropTypes.number,
    additionalInstructions: PropTypes.string,
    listType: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.any),
};

export default class GenericList extends React.Component {
    constructor(props) {
        super(props);
        this.addItem = this.addItem.bind(this);
        this.removeItem = this.removeItem.bind(this);
        this.handleTypeChange = this.handleTypeChange.bind(this);
        this.handleAmountChange = this.handleAmountChange.bind(this);
    }

    canAdd() {
        const { options, items, maxQuota } = this.props;

        if (this.computeTotal(items) === maxQuota) {
            return false;
        }

        // return false if no type selected
        for (let i = 0; i < items.length; i++) {
            const { type } = items[i];
            if (!type) {
                return false;
            }
        }

        // return false if all types selected (works cuz we only allow 1 type selected)
        if (Object.keys(options).length === items.length) return false;

        return true;
    }

    addItem() {
        const { items, handleListChange } = this.props;

        const newItem = {
            id: getNewId(),
            type: '',
            amount: 0,
            removable: true,
        };

        const newList = [...items, newItem];
        handleListChange(newList);
    }

    removeItem(index) {
        const { items, handleListChange } = this.props;

        const newList = [
            ...items.slice(0, index),
            ...items.slice(index + 1, items.length),
        ];
        const total = this.computeTotal(newList);

        handleListChange(newList, total);
    }

    handleTypeChange(index, type) {
        const { items, handleListChange } = this.props;

        // Can't select placeholder option & No duplicate types in list allowed
        if (!type || items.find((item) => item.type === type)) return;

        const newItems = cloneDeep(items);
        newItems[index].type = type;

        handleListChange(newItems);
    }

    handleAmountChange(index, amount) {
        const { items, maxQuota, handleListChange } = this.props;

        const newItems = cloneDeep(items);
        newItems[index].amount = parseInt(amount, 10);
        const itemType = newItems[index].type;
        if (!itemType || itemType === '0') return;

        const total = this.computeTotal(newItems);
        if (maxQuota && total <= maxQuota) {
            handleListChange(newItems, total);
        } else if (!maxQuota) {
            handleListChange(newItems, total);
        }
    }

    computeTotal(items = []) {
        return items.reduce((total, item) => {
            return total + parseInt(item.amount, 10);
        }, 0);
    }

    renderItemsList() {
        const { items, listType, options } = this.props;
        if (!items) return null;

        return items.map((item, i) => (
            <GenericItem
                key={item.id}
                innerRef={React.createRef()}
                item={item}
                index={i}
                handleTypeChange={(type) => this.handleTypeChange(i, type)}
                handleAmountChange={(amt) => this.handleAmountChange(i, amt)}
                removeItem={() => this.removeItem(i)}
                itemType={listType}
                options={options}
            />
        ));
    }

    render() {
        const { items, maxQuota, listType, additionalInstructions } = this.props;

        const btnText = `Add ${items.length ? 'another' : 'a'} ${listType}`;
        const messageTxt = `Enter the type & number of ${listType}(s) you will be bringing.`;
        const totalTxt = `${this.computeTotal(items)} / ${Number.isInteger(maxQuota) ? maxQuota : 'Unlimited'}`;

        return (
            <div className="per-equipment-form per-section-group">
                <div className="rec-order-detail-subsection-title">
                    {jsUcfirst(listType)}
                </div>
                <p>
                    {messageTxt}
                    <b>{additionalInstructions}</b>
                </p>
                <div className="per-total-count-wrapper">
                    <span className="per-total-count">
                        Total {listType}(s): <b>{totalTxt}</b>
                    </span>
                </div>
                <div className="per-form-group">{this.renderItemsList()}</div>
                <div className="rec-form-item-wrap">
                    <Button
                        type="button"
                        onClick={this.addItem}
                        appearance="link"
                        iconBeforeElement={<Icon iconName="add-circle" />}
                        className={`add-${listType}-link`}
                        isDisabled={!this.canAdd()}
                    >
                        {btnText}
                    </Button>
                </div>
            </div>
        );
    }
}

GenericList.propTypes = propTypes;
